import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ArrowLeft, List, TrendingUp, Trash2, Pencil, Columns } from 'lucide-react';
import { Button } from './ui/button';
import type { WeldPass } from './PassViewTable';
import WeldPassDetailTable from './WeldPassDetailTable';
import PassViewGraph from './ViewGraph';
import { usePassViewDetails, useCurrentProject } from '../redux/WeldData/hooks/useWeldData';
import { updateWeldNumber } from '../redux/WeldData/actions';
import { UpdateWeldNumberDialog } from '../features/project/components/dashboard/production/UpdateWeldNumberDialog';
import { formatTimeForDisplay, formatDuration, formatTimestamp, formatDateTime } from '../lib/formatUtils';
import { formatTimestampForDisplay, convertWeldDataValue, getParameterUnitSuffix, getUnitFromSettings } from '../utils/unitUtils';
import { ColumnSelectorModal, type ColumnDefinition } from './ui/ColumnSelectorModal';
import { createUpdateUserTableLayout, getAllUserTableLayouts } from '../redux/UserTableLayout/actions';

interface WeldPassDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  passData: WeldPass;
  clickedRowData?: any;
  weldData?: any; // Add weld data prop to access job_number
  statusCalculationMethod?: string;
  canEdit?: boolean;
}

// Column definitions for WeldPassDetailTable (same structure as TiltViewDetailTable)
const WELD_PASS_DETAIL_COLUMNS: ColumnDefinition[] = [
  // Sticky columns
  { key: 'sl-no', label: 'Sl.no', category: 'Basic Info' },
  { key: 'status', label: 'Status', category: 'Basic Info' },
  { key: 'event', label: 'Event', category: 'Basic Info' },
  { key: 'time', label: 'Time', category: 'Basic Info' },
  // Scrollable columns
  { key: 'tilt', label: 'Tilt', category: 'Basic Info' },
  { key: 'pass', label: 'Pass', category: 'Basic Info' },
  { key: 'pipe', label: 'Pipe', category: 'Basic Info' },
  { key: 'zone', label: 'Zone', category: 'Basic Info' },
  { key: 'distance', label: 'Distance', category: 'Basic Info' },
  // Parameter columns
  { key: 'travel-speed', label: 'Travel Speed', category: 'Parameters' },
  { key: 'voltage', label: 'Voltage', category: 'Parameters' },
  { key: 'current', label: 'Current', category: 'Parameters' },
  { key: 'wire-speed', label: 'Wire Speed', category: 'Parameters' },
  { key: 'oscillation-width', label: 'Oscillation Width', category: 'Parameters' },
  { key: 'target', label: 'Target', category: 'Parameters' },
  { key: 'horizontal-bias', label: 'Horizontal Bias', category: 'Parameters' },
  { key: 'frequency', label: 'Frequency', category: 'Parameters' },
  { key: 'total-wire-consumed', label: 'Total Wire Consumed', category: 'Parameters' },
  { key: 'true-energy', label: 'True Energy', category: 'Parameters' },
  { key: 'heat', label: 'Heat', category: 'Parameters' },
  { key: 'head-angle', label: 'Head Angle', category: 'Parameters' },
  { key: 'arc-impedance', label: 'Arc Impedance', category: 'Parameters' },
];

// Table name for API
const TABLE_NAME = 'weldPassDetail';

// Helper function to get all column keys
const getAllColumnKeys = (columns: ColumnDefinition[]): string[] => {
  const keys: string[] = [];
  columns.forEach(col => {
    keys.push(col.key);
    if (col.subColumns) {
      col.subColumns.forEach(sub => keys.push(sub.key));
    }
  });
  return keys;  
};

const WeldPassDetailModal: React.FC<WeldPassDetailModalProps> = ({ isOpen, onClose, passData, clickedRowData, weldData, statusCalculationMethod, canEdit = true }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('table');
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [showRowDetails, setShowRowDetails] = useState(true);
  const [showAllInfo, setShowAllInfo] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [weldToUpdate, setWeldToUpdate] = useState<any>(null);

  const updateWeldNumberLoading = useSelector((state: any) => state.weldData?.updateWeldNumberLoading || false);
  const updateWeldNumberSuccess = useSelector((state: any) => state.weldData?.updateWeldNumberSuccess || false);

  const { passViewDetails, passViewDetailsLoading, passViewDetailsError, loadPassViewDetails, clearPassViewDetailsData } = usePassViewDetails();
  const { currentProject } = useCurrentProject();

  // Get user table layouts from Redux state
  const userTableLayouts = useSelector((state: any) => state.userTableLayout?.layouts || []);
  const layoutsLoading = useSelector((state: any) => state.userTableLayout?.loading || false);

  // Store layout ID
  const layoutIdRef = useRef<number | null>(null);

  // Store default columns from API (for Reset button in ColumnSelectorModal)
  const [defaultColumnsFromAPI, setDefaultColumnsFromAPI] = useState<string[] | null>(null);

  // Column visibility state - initialize with all columns, will be updated from API
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => 
    getAllColumnKeys(WELD_PASS_DETAIL_COLUMNS)
  );

  // Column selector modal visibility state
  const [isColumnModalOpen, setIsColumnModalOpen] = useState(false);

  // Load layouts from API when component mounts/opens
  useEffect(() => {
    if (isOpen) {
      // Saga will get userId from Redux state
      dispatch(getAllUserTableLayouts({ 
        table_name: 'weldPassDetail'
      }));
    }
  }, [isOpen, dispatch]);

  // Update column state when layouts are loaded from API
  useEffect(() => {
    if (userTableLayouts.length > 0) {
      // Find layout for weld pass detail (saga already filtered by user_id)
      const layout = userTableLayouts.find(
        (layout: any) => layout.table_name === TABLE_NAME
      );

      // Update layout ID, columns, and default columns from API
      if (layout) {
        layoutIdRef.current = layout.user_table_layout_id;
        if (layout.layout?.columns && Array.isArray(layout.layout.columns)) {
          setVisibleColumns(layout.layout.columns);
          // Store as default columns for Reset button
          setDefaultColumnsFromAPI(layout.layout.columns);
        }
      }
    }
  }, [userTableLayouts]);

  // Column visibility handler - save to API
  const handleColumnsSave = useCallback((selectedColumns: string[]) => {
    setVisibleColumns(selectedColumns);
    
    const payload: any = {
      table_name: TABLE_NAME,
      layout: {
        columns: selectedColumns
      }
    };
    
    // If we have an existing layout ID, include it for update
    if (layoutIdRef.current) {
      payload.user_table_layout_id = layoutIdRef.current;
    }
    
    // Saga will add userId from Redux state
    dispatch(createUpdateUserTableLayout(payload));
  }, [dispatch]);

  // Update layout ID when create/update succeeds
  const createUpdateSuccess = useSelector((state: any) => state.userTableLayout?.currentLayout);
  useEffect(() => {
    if (createUpdateSuccess && createUpdateSuccess.table_name === TABLE_NAME) {
      layoutIdRef.current = createUpdateSuccess.user_table_layout_id;
    }
  }, [createUpdateSuccess]);
 

  // Debug logging
 

  // Generate welduid_s_event_torch from pass data
  const generateWelduidEventTorch = (pass: WeldPass) => {
    // Based on the curl command example: "123456789012345678901234_1745759759_LEAD"
    // The format is: weld_uid_event_torch
    
    // Extract weld_uid from pass data - prefer bugNumber as it's likely the weld identifier
    let weldUid = pass.bugNumber || pass.id?.toString() || '123456789012345678901234';
    
    // Extract event - try to use start time timestamp first, then fallback to pass id
    let event = '1745759759'; // Default fallback
    if (pass.startTime) {
      try {
        const startDate = new Date(pass.startTime);
        const timestamp = Math.floor(startDate.getTime() / 1000);
        event = timestamp.toString();
      } catch (error) {
        // Fallback to pass id if timestamp parsing fails
        event = pass.id?.toString() || '1745759759';
      }
    } else if (pass.id) {
      // Use pass id as event if no start time
      event = pass.id.toString();
    }
    
    // Extract torch type from pass data
    let torch = 'LEAD'; // Default fallback
    if (pass.torch && pass.torch !== '-') {
      torch = pass.torch.toUpperCase();
    }
    
    const welduid_s_event_torch = `${weldUid}_${event}_${torch}`;
    return welduid_s_event_torch;
  };

  // Load pass view details when component mounts or when passData changes
  useEffect(() => {
    // Always load API data for the table, regardless of clickedRowData
    if (isOpen && passData) {
      // Use the welduid_s_event_torch from the pass data if available
      if (passData.welduid_s_event_torch) {
        // Determine torch type from pass data
        const torchType = passData.torch && passData.torch !== '-' ? passData.torch : 'Lead';
        
        // Use centralized project data - no need to pass projectId and projectName
        loadPassViewDetails(passData.welduid_s_event_torch, 'Pass', torchType);
      } else {
        // Fallback to construction if not available
        const welduidEventTorch = generateWelduidEventTorch(passData);
        const torchType = passData.torch && passData.torch !== '-' ? passData.torch : 'Lead';
        
        // Use centralized project data - no need to pass projectId and projectName
        loadPassViewDetails(welduidEventTorch, 'Pass', torchType);
      }
    }

    // Cleanup when component unmounts or closes
    return () => {
      if (!isOpen) {
        clearPassViewDetailsData();
        setSelectedRowIndex(null);
      }
    };
  }, [isOpen, passData, loadPassViewDetails, clearPassViewDetailsData]);

  // Get data from API response for header details
  const detailData = passViewDetails?.data || [];

  const firstRow = detailData[0] || {};

  // Keep title as DATA360 - don't change it
  // useEffect(() => {
  //   if (isOpen) {
  //     const jobNumber = passData.jobNumber || selectedRow?.job_number || weldData?.jobNumber?.[0] || passData.bugNumber || passData.id || 'N/A';
  //     const stationNumber = passData.stationNumber || 'Station';
  //     const bugType = passData.bugType || 'N/A';
  //     const torch = passData.torch || 'N/A';
  //     
  //     const title = `Weld: ${jobNumber} | ${stationNumber} > ${bugType} > ${torch}`;
  //     document.title = title;
  //   } else {
  //     // Reset title when modal closes
  //     document.title = 'DATA360';
  //   }
  // }, [isOpen, firstRow?.job_number, weldData?.jobNumber, passData]);

  // Reset selected row when data changes
  useEffect(() => {
    setSelectedRowIndex(null);
  }, [detailData]);
  
  // Calculate weld time from API data if available
  const calculateWeldTime = () => {
    if (detailData.length > 1) {
      const firstTime = detailData[0]?.time;
      const lastTime = detailData[detailData.length - 1]?.time;
      if (firstTime && lastTime) {
        const duration = (lastTime - firstTime) / 60; // Convert to minutes
        return formatDuration(duration);
      }
    }
    return passData.duration ? formatDuration(passData.duration) : '-';
  };

  // Get dynamic unit labels for basic details
  const getUnitLabel = (parameter: string): string => {
    return getParameterUnitSuffix(parameter);
  };

  // Convert diameter values from inches to display unit
  const convertDiameterValue = (value: any): string => {
    if (value === undefined || value === null || value === '') return '-';
    const numericValue = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(numericValue)) return value;
    const convertedValue = convertWeldDataValue(numericValue);
    return convertedValue.toFixed(2);
  };



  // Helper function to get wire diameter based on torch type
  const getWireDiameter = (row: any, torchType?: string) => {
    if (torchType?.toLowerCase() === 'lead') {
      return row.lead_wire_diameter || row.wire_diameter || '-';
    } else if (torchType?.toLowerCase() === 'trail') {
      return row.trail_wire_diameter || row.wire_diameter || '-';
    }
    return row.wire_diameter || '-';
  };

  // Helper function to get band diameter (GPS coordinates if GPS mode)
  const getBandDiameter = (row: any) => {
    // Check if GPS mode (has GPS coordinates)
    if (row.gps && (row.gps.Latitude || row.gps.Longitude)) {
      const lat = row.gps.Latitude || '';
      const lng = row.gps.Longitude || '';
      return lat && lng ? `${lat}, ${lng}` : '-';
    }
    return row.band_diameter || '-';
  };

  // Helper function to get GPS coordinates for display
  const getGPSCoordinates = (weldData: any) => {
    if (!weldData?.gps) return '-';
    
    try {
      let gpsInfo = weldData.gps;
      if (typeof weldData.gps === 'string') {
        gpsInfo = JSON.parse(weldData.gps);
      }
      
      const lat = gpsInfo?.Latitude || gpsInfo?.latitude;
      const lng = gpsInfo?.Longitude || gpsInfo?.longitude;
      
      if (lat && lng) {
        return `${parseFloat(lat).toFixed(6)}, ${parseFloat(lng).toFixed(6)}`;
      }
    } catch (error) {
    }
    
    return '-';
  };

  // Helper function to get band diameter label
  const getBandDiameterLabel = (row: any) => {
    if (row.gps && (row.gps.Latitude || row.gps.Longitude)) {
      return 'GPS Coordinates';
    }
    return 'Band Diameter (in)';
  };

  // Helper function to get logging distance (logging period for station 0)
  const getLoggingDistance = (row: any) => {
    if (row.station_number === 0) {
      return row.iwm?.Logging_period || row.logging_distance || '-';
    }
    return row.logging_distance || '-';
  };

  // Helper function to get logging distance label
  const getLoggingDistanceLabel = (row: any) => {
    if (row.station_number === 0) {
      return 'Logging Period';
    }
    return 'Logging Distance (in)';
  };

  // Helper function to get fixed column details (like Sl.no, Status, Event, etc.)
  const getFixedColumnDetails = (row: any) => {
    if (!row) return [];
    
    const details: Array<{ label: string; value: string }> = [];
    
    // Fixed columns from the table - handle both API data and clickedRowData
    details.push({ label: 'Pass Name', value: row.name || row.pass_name || '-' });
    details.push({ label: 'Status', value: row.status || '-' });
            details.push({ label: 'Start Time', value: row.startTime ? formatTimestampForDisplay(row.startTime) : '-' });
        details.push({ label: 'End Time', value: row.endTime ? formatTimestampForDisplay(row.endTime) : '-' });
    details.push({ label: 'Duration', value: row.duration || '-' });
    
    // Additional fields that might be available
    if (row.id) details.push({ label: 'ID', value: row.id.toString() });
    if (row.bugNumber) details.push({ label: 'Bug Number', value: row.bugNumber }); 
    if (row.pipeDiameter) details.push({ label: 'Pipe Diameter', value: row.pipeDiameter.toString() });
    if (row.bandDiameter) details.push({ label: 'Band Diameter', value: row.bandDiameter.toString() });
    if (row.loggingDistance) details.push({ label: 'Logging Distance', value: row.loggingDistance.toString() });
    if (row.wireDiameter) details.push({ label: 'Wire Diameter', value: row.wireDiameter.toString() });
    
    // API data specific fields
    if (row.event) details.push({ label: 'Event', value: row.event });
    if (row.job_number) details.push({ label: 'Job Number', value: row.job_number });
    if (row.time) details.push({ label: 'Time', value: formatTimestamp(row.time) });
    if (row.tilt !== undefined) details.push({ label: 'Tilt', value: row.tilt.toString() });
    if (row.pass_name) details.push({ label: 'Pass Name (API)', value: row.pass_name });
    if (row.zone_name) details.push({ label: 'Zone Name (API)', value: row.zone_name });
    
    // Status from API data
    if (row.instantaneous_status !== undefined) {
      const statusValue = row.instantaneous_status === 'TRUE' ? 'Pass' : row.instantaneous_status === 'FALSE' ? 'Fail' : '-';
      details.push({ label: 'Status (API)', value: statusValue });
    }
    
    return details;
  };



  // Get the selected row or first row for details section
  // Use clickedRowData if available, otherwise use API data
  const selectedRow = clickedRowData || (selectedRowIndex !== null ? detailData[selectedRowIndex] : detailData[0]);

  // Create header details based on view type
  let details: Array<{ label: string; value: string | number }> = [];

  // Priority: Use passData first (when available), then clickedRowData, then API data
  if (passData && Object.keys(passData).length > 0) {
    // Use passData object directly - this is the most reliable source
    details = [
      { 
        label: 'Job Number', 
        value: passData.jobNumber || selectedRow?.job_number || weldData?.jobNumber?.[0] || passData.bugNumber || passData.id || '-' 
      },
      { 
        label: 'Pass', 
        value: passData.name || '-' 
      },
      { 
        label: 'Station', 
        value: passData.stationNumber || '-' 
      },
      { 
        label: 'Bug Type', 
        value: passData.bugType || '-' 
      },
      { 
        label: 'Torch', 
        value: passData.torch || '-' 
      },
      { 
        label: 'Bug Number', 
        value: passData.bugNumber || '-' 
      },
      { 
        label: `Band Diameter (${getUnitLabel('band_diameter')})`, 
        value: convertDiameterValue(passData.bandDiameter) 
      },
      { 
        label: `Wire Diameter (${getUnitLabel('wire_diameter')})`, 
        value: convertDiameterValue(passData.wireDiameter) 
      },
      { 
        label: `Logging Distance (${getUnitLabel('logging_distance')})`, 
        value: convertDiameterValue(passData.loggingDistance) 
      },
      { 
        label: `Pipe Diameter (${getUnitLabel('pipe_diameter')})`, 
        value: convertDiameterValue(passData.pipeDiameter) 
      },
      { 
        label: 'Weld Start Time', 
        value: passData.startTime ? formatDateTime(passData.startTime) : '-' 
      },
      { 
        label: 'Weld End Time', 
        value: passData.endTime ? formatDateTime(passData.endTime) : '-' 
      },
      { 
        label: 'GPS (deg)', 
        value: getGPSCoordinates(weldData) 
      },
      { 
        label: 'Auto Increment', 
        value: passData.autoincrement || '-' 
      },
      { 
        label: 'Weld Time', 
        value: passData.duration ? formatDuration(passData.duration) : '-' 

      },
      { 
        label: 'Welder Id', 
        value: passData.welderId ? passData.welderId : '-' 

      },
   
      // Add any missing filters from passData
    ];
  } else if (clickedRowData) {
    // Pass View: Read from clicked row object
    const torchType = clickedRowData.torch || 'Lead';
    
    details = [
      { 
        label: 'Job Number', 
        value: clickedRowData.jobNumber || clickedRowData.job_number || selectedRow?.job_number || weldData?.jobNumber?.[0] || clickedRowData.bugNumber || clickedRowData.bug_number || '-' 
      },
      { 
        label: 'Pass', 
        value: clickedRowData.name || clickedRowData.pass_name || '-' 
      },
      { 
        label: 'Station', 
        value: clickedRowData.stationNumber || clickedRowData.station_number || '-' 
      },
      { 
        label: 'Bug Type', 
        value: clickedRowData.bugType || clickedRowData.bug_type || '-' 
      },
      { 
        label: 'Torch', 
        value: clickedRowData.torch || torchType || '-' 
      },
      { 
        label: 'Bug Number', 
        value: clickedRowData.bugNumber || clickedRowData.bug_number || '-' 
      },
      { 
        label: getBandDiameterLabel(clickedRowData), 
        value: getBandDiameter(clickedRowData) 
      },
      { 
        label: `Wire Diameter (${getUnitLabel('wire_diameter')})`, 
        value: convertDiameterValue(getWireDiameter(clickedRowData, torchType)) 
      },
      { 
        label: getLoggingDistanceLabel(clickedRowData), 
        value: convertDiameterValue(getLoggingDistance(clickedRowData)) 
      },
      { 
        label: `Pipe Diameter (${getUnitLabel('pipe_diameter')})`, 
        value: convertDiameterValue(clickedRowData.pipeDiameter || clickedRowData.pipe_diameter) 
      },
      { 
        label: 'Weld Start Time', 
        value: clickedRowData.s_time ? formatDateTime(clickedRowData.s_time * 1000) : (clickedRowData.startTime ? formatDateTime(clickedRowData.startTime) : '-') 
      },
      { 
        label: 'Weld End Time', 
        value: clickedRowData.c_time ? formatDateTime(clickedRowData.c_time * 1000) : (clickedRowData.endTime ? formatDateTime(clickedRowData.endTime) : '-') 
      },
      { 
        label: 'GPS (deg)', 
        value: getGPSCoordinates(weldData) 
      },
      { 
        label: 'Autoincrement', 
        value: clickedRowData.autoIncrement || clickedRowData.auto_increment || '-' 
      },
      { 
        label: 'Weld Time', 
        value: clickedRowData.weld_time ? formatDuration(clickedRowData.weld_time) : (clickedRowData.duration ? formatDuration(clickedRowData.duration) : '-') 
      },
      { label: 'Welder ID', value: clickedRowData?.welderId || passData.welderId || '-' },
      { label: 'Welding System', value: clickedRowData?.weldingSystem || '-' },
      // Add any missing filters from clickedRowData
  
    ];
  } else {
    // Fallback: Use API data with passData fallbacks
    details = [
      { label: 'Job Number', value: firstRow.job_number || passData.jobNumber || selectedRow?.job_number || weldData?.jobNumber?.[0] || passData.bugNumber || '-' },
      { label: 'Pass', value: firstRow.pass_name || passData.name || '-' },
      { label: 'Station', value: firstRow.station_number || passData.stationNumber || '-' },
      { label: 'Bug Type', value: firstRow.bug_type || passData.bugType || '-' },
      { label: 'Torch', value: firstRow.torch || passData.torch || '-' },
      { label: 'Bug Number', value: firstRow.bug_number || passData.bugNumber || '-' },
      { label: `Band Diameter (${getUnitLabel('band_diameter')})`, value: convertDiameterValue(firstRow.band_diameter || passData.bandDiameter) },
      { label: `Wire Diameter (${getUnitLabel('wire_diameter')})`, value: convertDiameterValue(firstRow.wire_diameter || passData.wireDiameter) },
      { label: `Logging Distance (${getUnitLabel('logging_distance')})`, value: convertDiameterValue(firstRow.logging_distance || passData.loggingDistance) },
      { label: `Pipe Diameter (${getUnitLabel('pipe_diameter')})`, value: convertDiameterValue(firstRow.pipe_diameter || passData.pipeDiameter) },
              { label: 'Weld Start Time', value: firstRow.time ? formatDateTime(firstRow.time * 1000) : (passData.startTime ? formatDateTime(passData.startTime) : '-') },
              { label: 'Weld End Time', value: detailData.length > 1 ? formatDateTime(detailData[detailData.length - 1].time * 1000) : (passData.endTime ? formatDateTime(passData.endTime) : '-') },
      { label: 'GPS (deg)', value: getGPSCoordinates(weldData) },
      { label: 'Auto Increment', value: firstRow.auto_increment || passData.autoincrement || '-' },
     
      { label: 'Weld Time', value: calculateWeldTime() },
      { label: 'Welder ID', value: firstRow?.welderId || passData.welderId || '-' },
      { label: 'Welding System', value: firstRow?.weldingSystem || '-' },
      // Add any missing filters from API data
    ];
  } 

  // Color palette for badges (similar to index.tsx)
  const colorPalette = [
    'bg-green-200', 'bg-blue-100', 'bg-green-100', 'bg-red-100', 
    'bg-cyan-100', 'bg-teal-100', 'bg-indigo-100', 'bg-lime-100',
    'bg-pink-100', 'bg-rose-100', 'bg-emerald-100', 'bg-violet-100',
    'bg-amber-100', 'bg-sky-100', 'bg-stone-100', 'bg-slate-100',
    'bg-zinc-100', 'bg-purple-100', 'bg-orange-100', 'bg-yellow-100'
  ];

  // Convert details to infoItems with colors
  const infoItems = details.map((item, index) => ({
    ...item,
    bgColor: colorPalette[index % colorPalette.length]
  }));

  const visibleItems = showAllInfo ? infoItems : infoItems.slice(0, 7);

  // Handle update modal
  const handleOpenUpdateModal = (weld: any) => {
    setWeldToUpdate(weld);
    setUpdateModalOpen(true);
  };

  const handleCloseUpdateModal = () => {
    if (!updateWeldNumberLoading) {
      setUpdateModalOpen(false);
      setWeldToUpdate(null);
    }
  };

  // Close modal on successful update
  useEffect(() => {
    if (updateWeldNumberSuccess) {
      handleCloseUpdateModal();
    }
  }, [updateWeldNumberSuccess]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[100vw] w-screen h-screen max-h-screen p-0 gap-0 overflow-hidden">
        <div className="flex flex-col h-full font-sans overflow-hidden">
          {/* Header */}
          <DialogHeader className="px-6 py-4 border-b flex flex-row items-center justify-between bg-white flex-shrink-0">
            <div className="flex items-center gap-4">
              <Button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                variant="outline"
              >
                <ArrowLeft className="h-5 w-5 text-gray-500" />
              </Button>
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl font-bold">{weldData?.weldNumber || passData.bugNumber || passData.id || '-'}</h1>
                <div className="text-xs sm:text-sm text-gray-600 mt-1 space-y-1">
                  <div className="flex flex-wrap gap-2">
                    {visibleItems.map((item, index) => (
                      <span key={index} className={`${item.bgColor} px-2 py-1 rounded text-xs`}>
                        {item.label}: {item.value}
                      </span>
                    ))}
                    {infoItems.length > 7 && (
                      <button
                        onClick={() => setShowAllInfo(!showAllInfo)}
                        className="bg-gray-200 hover:bg-gray-300 px-3 py-1 rounded text-xs font-medium transition-colors duration-200 flex items-center gap-1"
                      >
                        {showAllInfo ? 'Show Less' : `Show More (+${infoItems.length - 7})`}
                        <span className="text-xs">{showAllInfo ? '▲' : '▼'}</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </DialogHeader>

          {/* Tabs */}
          <div className="flex-1 flex flex-col min-h-0">
            {/* Show loader while data is loading */}
            {passViewDetailsLoading ? (
              <div className="flex-1 flex items-center justify-center bg-white">
                <div className="flex flex-col items-center space-y-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#EB1C37]"></div>
                  <p className="text-gray-600 text-lg font-medium">Loading pass view details...</p>
                  <p className="text-gray-400 text-sm">Please wait while we fetch the data</p>
                </div>
              </div>
            ) : passViewDetailsError ? (
              <div className="flex-1 flex items-center justify-center bg-white">
                <div className="flex flex-col items-center space-y-4 max-w-md text-center">
                  <div className="text-red-500 text-5xl">⚠️</div>
                  <p className="text-red-600 text-lg font-medium">Error loading pass view details</p>
                  <p className="text-gray-500 text-sm">{passViewDetailsError}</p>
                  <Button onClick={onClose} variant="outline">Close</Button>
                </div>
              </div>
            ) : (
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full h-full flex flex-col">
                <div className="sticky top-0 bg-white rounded-t-lg z-10 border-b border-gray-200 flex-shrink-0">
                  <div className="flex items-center justify-between px-1 md:px-4 py-2">
                    <TabsList className="h-auto flex-1 justify-start gap-2 md:gap-4 lg:gap-8 border-b bg-transparent py-2 flex-wrap">
                      <TabsTrigger value="table" className="data-[state=active]:border-[#EB1C37] data-[state=active]:text-[#EB1C37] relative px-1 pb-3 pt-2 text-sm md:text-base font-medium text-gray-500 hover:text-gray-700 data-[state=active]:border-b-[0.5px] whitespace-nowrap">
                        <List className="inline-block mr-1 h-4 w-4 align-text-bottom" /> Data Analysis
                      </TabsTrigger>
                      <TabsTrigger value="graph" className="data-[state=active]:border-[#EB1C37] data-[state=active]:text-[#EB1C37] relative px-1 pb-3 pt-2 text-sm md:text-base font-medium text-gray-500 hover:text-gray-700 data-[state=active]:border-b-[0.5px] whitespace-nowrap">
                        <TrendingUp className="inline-block mr-1 h-4 w-4 align-text-bottom" /> Graph View
                      </TabsTrigger>
                    </TabsList>
                    {activeTab === 'table' && (
                      <div className="flex gap-2 px-2">
                        <Button
                          onClick={() => setIsColumnModalOpen(true)}
                          className="flex items-center gap-2"
                          title="Configure columns"
                        >
                          <Columns className="h-4 w-4" />
                          <span className="hidden sm:inline">Columns</span>
                        </Button>
                        {passViewDetails?.data?.data.length > 0 && canEdit && (
                          <Button
                            title="Edit Weld Number"
                            onClick={() => handleOpenUpdateModal(weldData)}
                            disabled={!weldData}
                          >
                            <Pencil className="h-3 w-3" /> Edit Weld Number
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <TabsContent value="table" className="flex-1 min-h-0 overflow-hidden">
                  <div className="h-full px-4">
                    <WeldPassDetailTable 
                      detailData={passViewDetails?.data?.data || []}
                      onRowSelect={setSelectedRowIndex}
                      selectedRowIndex={selectedRowIndex}
                      loading={passViewDetailsLoading}
                      statusCalculationMethod={statusCalculationMethod}
                      visibleColumns={visibleColumns}
                    />
                  </div>
                </TabsContent>
                <TabsContent value="graph" className="flex-1 min-h-0 overflow-hidden">
                  <div className="h-full overflow-y-auto">
                    <div className="p-4 pb-20">
                      <PassViewGraph
                        detailData={passViewDetails?.data?.data || []}
                        projectNumber={passData.bugNumber || ''}
                        projectName={passData.name || ''}
                      />
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            )}
          </div>

          {/* Weld Number Update Modal */}
          <UpdateWeldNumberDialog
            open={updateModalOpen}
            onOpenChange={(open) => {
              if (!open) {
                handleCloseUpdateModal();
              }
            }}
            title="Update Weld Number"
            currentValue={weldToUpdate?.weldNumber || ''}
            showJobNumber={false}
            currentJobNumber={weldToUpdate?.wpsNumber || ''}
            onConfirm={(newValue, newJobNumber) => {
              if (weldToUpdate && newValue && !updateWeldNumberLoading) {
                const currentProjectId = currentProject?.project?.projectId;
                if (currentProjectId) {
                  const payload = {
                    project_id: currentProjectId,
                    old_weld_number: weldToUpdate.weldNumber,
                    new_weld_number: newValue,
                    isNewWeld: false,
                    weld_uid: passData.welduid_s_event_torch,
                    oldJobNumber: weldToUpdate.wpsNumber,
                    newJobNumber: newJobNumber,
                  };
                  dispatch(updateWeldNumber(payload));
                }
              }
            }}
            loading={updateWeldNumberLoading}
            maxWidth="3xl"
          />
        </div>
      </DialogContent>

      {/* Column Selector Modal */}
      <ColumnSelectorModal
        isOpen={isColumnModalOpen}
        onClose={() => setIsColumnModalOpen(false)}
        onSave={handleColumnsSave}
        columns={WELD_PASS_DETAIL_COLUMNS}
        selectedColumns={visibleColumns}
        title="Select Columns - Weld Pass Detail"
        defaultColumns={defaultColumnsFromAPI || getAllColumnKeys(WELD_PASS_DETAIL_COLUMNS)}
      />
    </Dialog>
  );
};

export default WeldPassDetailModal; 